import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  subscribeToVotingProject,
  submitVote,
  removeVote,
  hasDeviceVotedForProject,
  VotingProject,
} from "../../../services/votingService";
import {
  getDeviceFingerprint,
  markDeviceAsVoted,
  hasDeviceVoted,
} from "../../../utils/deviceFingerprint";

const Profile: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<VotingProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasVoted, setHasVoted] = useState(false);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState<string | null>(
    null
  );

  // Convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url: string): string => {
    if (!url) return "";

    // Handle different YouTube URL formats
    let videoId = "";

    // Regular watch URL: https://www.youtube.com/watch?v=VIDEO_ID
    const watchMatch = url.match(/[?&]v=([^&]+)/);
    if (watchMatch) {
      videoId = watchMatch[1];
    }

    // Short URL: https://youtu.be/VIDEO_ID
    const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
    if (shortMatch) {
      videoId = shortMatch[1];
    }

    // Embed URL: https://www.youtube.com/embed/VIDEO_ID
    const embedMatch = url.match(/embed\/([^?&]+)/);
    if (embedMatch) {
      videoId = embedMatch[1];
    }

    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }

    // If we can't parse it, return the original URL
    return url;
  };

  // Initialize device fingerprint
  useEffect(() => {
    const initFingerprint = async () => {
      const fingerprint = await getDeviceFingerprint();
      setDeviceFingerprint(fingerprint);
    };
    initFingerprint();
  }, []);

  // Subscribe to real-time project updates
  useEffect(() => {
    if (!projectId) {
      setLoading(false);
      return;
    }

    const unsubscribe = subscribeToVotingProject(
      projectId,
      (updatedProject) => {
        setProject(updatedProject);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [projectId]);

  // Check voting state when project or fingerprint changes
  useEffect(() => {
    const checkVotingState = async () => {
      if (!projectId || !deviceFingerprint) return;

      const voted = await hasDeviceVotedForProject(
        projectId,
        deviceFingerprint
      );
      setHasVoted(voted);
    };

    checkVotingState();
  }, [projectId, deviceFingerprint, project]);

  const handleVote = async () => {
    if (!projectId || !deviceFingerprint) {
      alert("Device fingerprint not initialized. Please refresh the page.");
      return;
    }

    if (votingInProgress) {
      return;
    }

    setVotingInProgress(true);

    try {
      if (hasVoted) {
        // Remove vote
        const result = await removeVote(projectId, deviceFingerprint);
        if (result.success) {
          setHasVoted(false);
          await hasDeviceVoted(projectId).then((localVoted) => {
            if (localVoted) {
              // Clear local storage vote
              const votedProjects = JSON.parse(
                localStorage.getItem("votedProjects") || "[]"
              ) as string[];
              const filtered = votedProjects.filter((id) => id !== projectId);
              localStorage.setItem("votedProjects", JSON.stringify(filtered));
            }
          });
        } else {
          alert(result.message);
        }
      } else {
        // Submit vote
        const result = await submitVote(projectId, deviceFingerprint);
        if (result.success) {
          setHasVoted(true);
          await markDeviceAsVoted(projectId);
        } else {
          alert(result.message);
        }
      }
    } catch (error) {
      console.error("Error voting:", error);
      alert("An error occurred while processing your vote.");
    } finally {
      setVotingInProgress(false);
    }
  };

  if (!projectId) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-6 py-8 min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center text-[var(--text-secondary)]">
          <p className="text-xl">No project ID provided.</p>
          <p className="text-sm mt-2">
            Please navigate to a project profile page.
          </p>
        </div>
      </div>
    );
  }

  if (loading && !project) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-6 py-8 min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center text-[var(--text-primary)]">
          <div className="text-xl">Loading project...</div>
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="w-full max-w-[1400px] mx-auto px-6 py-8 min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="text-center text-[var(--text-secondary)]">
          <p className="text-xl">Project not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-[1400px] mx-auto  py-8 min-h-[calc(100vh-64px)] sm:w-full">
      <style>
        {`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
            }
            50% {
              transform: scale(1.1);
            }
          }
          .animate-pulse-custom {
            animation: pulse 2s ease-in-out infinite;
          }
        `}
      </style>
      <div className="flex flex-col gap-10">
        {/* Profile Card Section */}
        <div className="flex justify-center w-full">
          <div className="w-full max-w-[900px] bg-white dark:bg-slate-900 rounded-xl overflow-hidden">
            {/* Cover Photo */}
            <div className="w-full h-[260px] bg-gray-200 dark:bg-slate-700 relative">
              {project?.poster ? (
                <img
                  src={project.poster}
                  className="w-full h-full object-cover"
                  alt={project.name}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400 text-6xl">
                  <i className="fa-solid fa-image"></i>
                </div>
              )}
            </div>

            {/* Profile Image + Vote Box */}
            <div className="relative px-6">
              {/* Circular profile image */}
              <div className="absolute -top-[70px] left-6">
                <div className="w-[140px] h-[140px] rounded-full overflow-hidden border-4 border-white dark:border-slate-900 shadow-xl bg-white">
                  {project?.poster ? (
                    <img
                      src={project.poster}
                      className="w-full h-full object-cover"
                      alt="Project"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400 text-5xl">
                      <i className="fa-solid fa-image"></i>
                    </div>
                  )}
                </div>
              </div>

              {/* Vote Section */}
              <div className="flex justify-end w-full mt-6">
                <button
                  disabled={votingInProgress || loading || !project}
                  onClick={handleVote}
                  className={`flex items-center gap-3 px-6 py-2.5 rounded-lg font-semibold text-white transition-all ${
                    hasVoted
                      ? "bg-red-500 hover:bg-red-600"
                      : "bg-blue-600 hover:bg-blue-700"
                  } disabled:opacity-50`}
                >
                  <i
                    className={`fa-solid ${
                      votingInProgress
                        ? "fa-spinner fa-spin"
                        : hasVoted
                        ? "fa-heart"
                        : "fa-heart-circle-plus"
                    }`}
                  ></i>
                  {votingInProgress
                    ? "Processing..."
                    : hasVoted
                    ? "Voted"
                    : "Vote"}
                </button>
              </div>
            </div>

            {/* Project Details */}
            <div className="px-6 mt-10 pb-6">
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white">
                {project?.name || "Project Name"}
              </h2>

              <p className="text-gray-500 dark:text-gray-400 mt-1">
                {project?.group || "Group Information"}
              </p>

              <div className="flex items-center gap-2 mt-3 text-gray-400 text-sm">
                <i className="fa-solid fa-calendar"></i>
                {project?.createdAt &&
                  new Date(project.createdAt.toMillis()).toLocaleDateString()}
              </div>

              {/* Vote Count */}
              <div className="mt-4 flex items-center gap-2">
                <i className="fa-solid fa-heart text-red-500 text-xl"></i>
                <span className="font-bold text-lg">
                  {loading ? "..." : project?.voteCount || 0}
                </span>
                <span className="text-gray-500 uppercase text-xs">Votes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Projects Section */}
        {project.youtubeUrl && (
          <div className="w-full flex justify-center">
            <div className="w-full max-w-[900px] flex flex-col items-center gap-5">
              <h2 className="text-[2rem] font-bold text-[var(--text-primary)] m-0 flex items-center gap-3">
                <i className="fa-brands fa-youtube text-[var(--primary-color)]"></i>
                Project Preview
              </h2>
              <div className="w-full aspect-video">
                <iframe
                  width="100%"
                  height="100%"
                  src={getYouTubeEmbedUrl(project.youtubeUrl)}
                  title={project.name || "Project Preview"}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="rounded-lg"
                ></iframe>
              </div>
            </div>
          </div>
        )}

        {/* Social Media Links */}
        {/* <div className="w-full flex flex-col items-center gap-6 p-10 bg-[rgba(255,255,255,0.95)] dark:bg-[rgba(30,41,59,0.95)] backdrop-blur-[20px] rounded-[20px] shadow-[0_8px_32px_rgba(37,99,235,0.15)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)] border border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)]">
          <h3 className="text-[1.5rem] font-semibold text-[var(--text-primary)] m-0">
            Connect with me
          </h3>
          <ul className="flex flex-wrap justify-center items-center gap-8 list-none m-0 p-0">
            <li className="m-0">
              <a
                href="#"
                className="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(15,23,42,0.9)] backdrop-blur-[10px] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] text-[var(--text-primary)] text-[1.5rem] no-underline transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 hover:scale-110 hover:shadow-[0_8px_24px_rgba(0,0,0,0.2)] hover:bg-[#1877f2] hover:text-white hover:border-[#1877f2] hover:shadow-[0_8px_24px_rgba(24,119,242,0.4)]"
                aria-label="Facebook"
              >
                <i className="fa-brands fa-facebook"></i>
              </a>
            </li>
            <li className="m-0">
              <a
                href="#"
                className="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(15,23,42,0.9)] backdrop-blur-[10px] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] text-[var(--text-primary)] text-[1.5rem] no-underline transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 hover:scale-110 hover:shadow-[0_8px_24px_rgba(221,42,123,0.4)] hover:bg-gradient-to-br hover:from-[#f58529] hover:via-[#feda77] hover:to-[#dd2a7b] hover:text-white hover:border-transparent"
                aria-label="Instagram"
              >
                <i className="fa-brands fa-instagram"></i>
              </a>
            </li>
            <li className="m-0">
              <a
                href="#"
                className="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(15,23,42,0.9)] backdrop-blur-[10px] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] text-[var(--text-primary)] text-[1.5rem] no-underline transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 hover:scale-110 hover:shadow-[0_8px_24px_rgba(0,0,0,0.4)] hover:bg-[#000000] hover:text-white hover:border-[#000000]"
                aria-label="Twitter"
              >
                <i className="fa-brands fa-x-twitter"></i>
              </a>
            </li>
            <li className="m-0">
              <a
                href="#"
                className="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(15,23,42,0.9)] backdrop-blur-[10px] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] text-[var(--text-primary)] text-[1.5rem] no-underline transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 hover:scale-110 hover:shadow-[0_8px_24px_rgba(24,23,23,0.4)] hover:bg-[#181717] hover:text-white hover:border-[#181717]"
                aria-label="GitHub"
              >
                <i className="fa-brands fa-github"></i>
              </a>
            </li>
            <li className="m-0">
              <a
                href="#"
                className="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(15,23,42,0.9)] backdrop-blur-[10px] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] text-[var(--text-primary)] text-[1.5rem] no-underline transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 hover:scale-110 hover:shadow-[0_8px_24px_rgba(255,0,80,0.4)] hover:bg-[#000000] hover:text-[#ff0050] hover:border-[#ff0050]"
                aria-label="TikTok"
              >
                <i className="fa-brands fa-tiktok"></i>
              </a>
            </li>
            <li className="m-0">
              <a
                href="#"
                className="flex items-center justify-center w-[60px] h-[60px] rounded-full bg-[rgba(255,255,255,0.9)] dark:bg-[rgba(15,23,42,0.9)] backdrop-blur-[10px] border-2 border-[rgba(59,130,246,0.2)] dark:border-[rgba(59,130,246,0.3)] text-[var(--text-primary)] text-[1.5rem] no-underline transition-all duration-300 shadow-[0_4px_12px_rgba(0,0,0,0.1)] hover:-translate-y-1.5 hover:scale-110 hover:shadow-[0_8px_24px_rgba(10,102,194,0.4)] hover:bg-[#0a66c2] hover:text-white hover:border-[#0a66c2]"
                aria-label="LinkedIn"
              >
                <i className="fa-brands fa-linkedin"></i>
              </a>
            </li>
          </ul>
        </div> */}
      </div>
    </div>
  );
};

export default Profile;
